import { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    BatteryCharging, Sun, Snowflake, Coffee, IceCream, Flame,
    UtensilsCrossed, Beer, Pizza, Hand, Send, Check, Ruler, Sparkles,
    Umbrella, CloudRain, CloudSun, X, Palette, Type, Lightbulb, Zap,
    ArrowRight, MessageCircle,
} from "lucide-react";
import { api, API } from "@/lib/api";
import { cn } from "@/lib/utils";
import { FileDown, Share2 } from "lucide-react";
import Cart3D from "@/components/Cart3D";
import BrandingProposal from "@/components/BrandingProposal";
import { usePageContent, useContentList, useSetting } from "@/context/CmsContext";
import { useLocale } from "@/context/LocaleContext";
import useSeo from "@/lib/useSeo";
import { mergeListById, resolveIcon } from "@/lib/cmsHelpers";

/* English overrides for base option lists (applied when lang = en) */
const EN_SIZES = {
    "1.98m": { title: "Large", capacity: "2-person operation", description: "Extended counter for multi-equipment. Ideal for coffee + freezer + grill at the same time." },
    "1.35m": { title: "Compact", capacity: "1-person operation", description: "Perfect for single-product (ice cream, crêpes, hot dogs) and tight spaces." },
};
const EN_PRIMARY = {
    coffee_machine: { label: "Coffee machine", hint: "2-group professional" },
    ice_cream_machine: { label: "Ice cream machine", hint: "Soft-serve / artisanal" },
    grill: { label: "Grill", hint: "Stainless steel plancha" },
    crepe_maker: { label: "Crêpe maker", hint: "40 cm round plate" },
    beer_machine: { label: "Beer tap", hint: "Integrated 30 L keg" },
    pizza_oven: { label: "Pizza oven", hint: "Up to 400 °C" },
    hotdog_maker: { label: "Hot dog machine", hint: "Steam + grill" },
};
const EN_EXTRAS = { freezer: { label: "Freezer", hint: "-18 °C independent compartment" } };
const EN_BATTERIES = {
    none: { label: "No battery", hint: "External power only" },
    essencial: { label: "Essential", hint: "4-6 h autonomy" },
    standard: { label: "Standard", hint: "8-12 h autonomy" },
    premium: { label: "Premium", hint: "16-20 h autonomy" },
};
const EN_SOLARS = {
    none: { label: "No panel", hint: "Battery/grid only" },
    basic: { label: "Basic", hint: "Light recharge on the go" },
    standard: { label: "Standard", hint: "Full recharge on the go" },
    premium: { label: "Premium", hint: "Maximum outdoor autonomy" },
};
const EN_LIGHTING = {
    perimeter: { label: "Perimeter LED", hint: "LED strip around counter and canopy" },
    spots: { label: "Directed spots", hint: "Product-focused lighting" },
    ambient: { label: "RGB ambient", hint: "Interior LED with custom colours" },
    led_warm: { label: "Warm LED", hint: "Warm ambient lighting" },
    led_cool: { label: "Cool LED", hint: "Cool white lighting" },
    neon: { label: "Neon", hint: "Statement neon accents" },
    spotlights: { label: "Spotlights", hint: "Product-focused lighting" },
};
const EN_CANOPY_TYPES = {
    awning: { label: "Classic awning", hint: "Striped, angled over posts" },
    flat: { label: "Flat awning", hint: "Flat with scalloped hem" },
    umbrella: { label: "Umbrella", hint: "Centred round parasol" },
    none: { label: "No canopy", hint: "Exposed structure only" },
};
const EN_CANOPY_COLORS = {
    navy_gold: { label: "Navy & Gold" }, yellow_green: { label: "Yellow & Green" },
    red_white: { label: "Red & White" }, black_white: { label: "Black & White" }, cream: { label: "Solid cream" },
};
const locList = (list, map, en) => (en ? list.map((o) => ({ ...o, ...(map[o.id] || {}) })) : list);

/* ------------------------------------------------------------------ */
/*  Theme → Primary equipment + Brand name mapping                    */
/*  (used when arriving from /simulador?tema=<id>)                    */
/* ------------------------------------------------------------------ */
const THEME_PRESETS = {
    cafes:      { primary: "coffee_machine",   brand: "CAFÉ" },
    gelados:    { primary: "ice_cream_machine", brand: "GELATO" },
    churros:    { primary: "crepe_maker",      brand: "CHURROS" },
    doces:      { primary: "crepe_maker",      brand: "SWEETS" },
    drinks:     { primary: "beer_machine",     brand: "BAR" },
    sanduiches: { primary: "grill",            brand: "DELI" },
    paes:       { primary: "pizza_oven",       brand: "PADARIA" },
    sushis:     { primary: null,               brand: "SUSHI" },
    jornais:    { primary: null,               brand: "NEWS" },
    pipocas:    { primary: null,               brand: "POP" },
    crepes:     { primary: "crepe_maker",      brand: "CRÊPE" },
};

/* ------------------------------------------------------------------ */
/*  CONFIG / CATALOGUE                                                */
/* ------------------------------------------------------------------ */

const BASE_SIZES = [
    {
        id: "1.98m",
        title: "Grande",
        length: "1,98 m",
        counter: "0,90 m",
        capacity: "Operação 2 pessoas",
        description: "Bancada extensa para multi-equipamento. Ideal para café + freezer + grelhador em simultâneo.",
    },
    {
        id: "1.35m",
        title: "Compacto",
        length: "1,35 m",
        counter: "0,90 m",
        capacity: "Operação 1 pessoa",
        description: "Perfeito para single-product (gelados, crepes, hot dogs) e zonas com espaço limitado.",
    },
];

const BASE_PRIMARY_EQUIPMENT = [
    { id: "coffee_machine", label: "Máquina de café", icon: Coffee, hint: "2 grupos profissional" },
    { id: "ice_cream_machine", label: "Máquina de gelados", icon: IceCream, hint: "Soft-serve / artesanal" },
    { id: "grill", label: "Grelhador", icon: Flame, hint: "Plancha em aço inox" },
    { id: "crepe_maker", label: "Crepeira", icon: UtensilsCrossed, hint: "Placa redonda 40 cm" },
    { id: "beer_machine", label: "Máquina de cerveja", icon: Beer, hint: "Barril 30 L integrado" },
    { id: "pizza_oven", label: "Forno de pizza", icon: Pizza, hint: "Temperatura até 400 °C" },
    { id: "hotdog_maker", label: "Máquina de cachorros", icon: Hand, hint: "Vapor + grelha" },
];

const BASE_EXTRAS = [
    { id: "freezer", label: "Congelador", icon: Snowflake, hint: "Compartimento -18 °C, independente" },
];

const BASE_BATTERIES = [
    { id: "none", label: "Sem bateria", short: "—", bars: 0, capacity: "", hint: "Apenas alimentação externa" },
    { id: "essencial", label: "Essencial", short: "0,5 kWh", bars: 1, capacity: "0,5 kWh", hint: "Autonomia 4-6 h" },
    { id: "standard", label: "Standard", short: "1 kWh", bars: 2, capacity: "1 kWh", hint: "Autonomia 8-12 h" },
    { id: "premium", label: "Premium", short: "2 kWh", bars: 3, capacity: "2 kWh", hint: "Autonomia 16-20 h" },
];

const BASE_SOLARS = [
    { id: "none", label: "Sem painel", short: "—", widthPct: 0, power: "", hint: "Apenas bateria/rede" },
    { id: "basic", label: "Básico", short: "80 W", widthPct: 55, power: "80 W", hint: "Recarga leve em movimento" },
    { id: "standard", label: "Standard", short: "160 W", widthPct: 80, power: "160 W", hint: "Recarga total em movimento" },
    { id: "premium", label: "Premium", short: "240 W", widthPct: 100, power: "240 W", hint: "Autonomia máxima ao ar livre" },
];

const BASE_LIGHTING = [
    { id: "perimeter", label: "LED perimetral", hint: "Fita LED à volta da bancada e toldo" },
    { id: "spots", label: "Spots dirigidos", hint: "Iluminação focada no produto" },
    { id: "ambient", label: "Ambiente RGB", hint: "LED interior com cores personalizáveis" },
];

const BASE_CANOPY_TYPES = [
    { id: "awning", label: "Toldo clássico", hint: "Inclinado listrado sobre postes", icon: CloudSun },
    { id: "flat", label: "Toldo recto", hint: "Plano com bainha em festo", icon: CloudRain },
    { id: "umbrella", label: "Guarda-sol", hint: "Chapéu redondo centralizado", icon: Umbrella },
    { id: "none", label: "Sem cobertura", hint: "Apenas estrutura exposta", icon: X },
];

const BASE_CANOPY_COLORS = [
    { id: "navy_gold", label: "Navy & Dourado", primary: "#1E3A5F", accent: "#F59E0B" },
    { id: "yellow_green", label: "Amarelo & Verde", primary: "#065F46", accent: "#FBBF24" },
    { id: "red_white", label: "Vermelho & Branco", primary: "#DC2626", accent: "#F8FAFC" },
    { id: "black_white", label: "Preto & Branco", primary: "#171717", accent: "#F8FAFC" },
    { id: "cream", label: "Creme sólido", primary: "#D6CDB2", accent: "#A89973" },
];

const MAX_BRAND = 24; // chars on the cart visual

/* ------------------------------------------------------------------ */
/*  Form validation helpers                                            */
/* ------------------------------------------------------------------ */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_REGEX = /^\d{9}$/;
const CIDADES_PT = [
    "Aveiro", "Beja", "Braga", "Bragança", "Castelo Branco", "Coimbra",
    "Évora", "Faro", "Guarda", "Leiria", "Lisboa", "Portalegre", "Porto",
    "Santarém", "Setúbal", "Viana do Castelo", "Vila Real", "Viseu",
    "Funchal", "Ponta Delgada", "Angra do Heroísmo", "Horta",
    "Almada", "Amadora", "Barcelos", "Cascais", "Famalicão", "Gondomar",
    "Guimarães", "Loures", "Maia", "Matosinhos", "Odivelas", "Oeiras",
    "Sintra", "Vila Nova de Gaia", "Outra",
];

/* ------------------------------------------------------------------ */
/*  PAGE                                                              */
/* ------------------------------------------------------------------ */

export default function Simulador() {
    const { t } = usePageContent("simulador");
    const { tr, en } = useLocale();
    const whatsappNumber = useSetting("whatsapp_number", "351900000000");
    const whatsappMessage = useSetting("whatsapp_message", "Olá NOVIFOOD, gostaria de saber mais");
    const sizesCms = useContentList("sim_sizes", []);

    useSeo({
        title: tr("Simulador — Configure o seu food cart NOVIFOOD", "Configurator — Build your NOVIFOOD food cart"),
        description: tr(
            "Simulador interactivo: escolha tamanho, equipamento, energia (solar/bateria), iluminação, cobertura e branding. Receba orçamento personalizado em 48h.",
            "Interactive configurator: choose size, equipment, energy (solar/battery), lighting, canopy and branding. Get a personalised quote within 48h."
        ),
        path: "/simulador",
    });
    const primaryCms = useContentList("sim_primary_equipment", []);
    const extrasCms = useContentList("sim_extras", []);
    const batteriesCms = useContentList("sim_batteries", []);
    const solarsCms = useContentList("sim_solars", []);
    const lightingCms = useContentList("sim_lighting", []);
    const canopyTypesCms = useContentList("sim_canopy_types", []);
    const canopyColorsCms = useContentList("sim_canopy_colors", []);

    const SIZES = useMemo(() => locList(mergeListById(BASE_SIZES, sizesCms), EN_SIZES, en), [sizesCms, en]);
    const PRIMARY_EQUIPMENT = useMemo(() => locList(mergeListById(BASE_PRIMARY_EQUIPMENT, primaryCms), EN_PRIMARY, en), [primaryCms, en]);
    const EXTRAS = useMemo(() => locList(mergeListById(BASE_EXTRAS, extrasCms), EN_EXTRAS, en), [extrasCms, en]);
    const BATTERIES = useMemo(() => locList(mergeListById(BASE_BATTERIES, batteriesCms), EN_BATTERIES, en), [batteriesCms, en]);
    const SOLARS = useMemo(() => locList(mergeListById(BASE_SOLARS, solarsCms), EN_SOLARS, en), [solarsCms, en]);
    const LIGHTING = useMemo(() => locList(mergeListById(BASE_LIGHTING, lightingCms), EN_LIGHTING, en), [lightingCms, en]);
    const CANOPY_TYPES = useMemo(() => locList(mergeListById(BASE_CANOPY_TYPES, canopyTypesCms), EN_CANOPY_TYPES, en), [canopyTypesCms, en]);
    /* For canopy colors: CMS uses c1/c2 naming, base uses primary/accent. Map c1/c2 -> primary/accent */
    const CANOPY_COLORS = useMemo(() => {
        const normalized = (canopyColorsCms || []).map((c) => ({
            id: c.id,
            label: c.label,
            primary: c.primary || c.c1,
            accent: c.accent || c.c2,
        }));
        return locList(mergeListById(BASE_CANOPY_COLORS, normalized), EN_CANOPY_COLORS, en);
    }, [canopyColorsCms, en]);

    const PRIMARY_MAP = useMemo(() => Object.fromEntries(PRIMARY_EQUIPMENT.map((p) => [p.id, p])), [PRIMARY_EQUIPMENT]);
    const EXTRA_MAP = useMemo(() => Object.fromEntries(EXTRAS.map((e) => [e.id, e])), [EXTRAS]);
    const BATTERY_MAP = useMemo(() => Object.fromEntries(BATTERIES.map((b) => [b.id, b])), [BATTERIES]);
    const SOLAR_MAP = useMemo(() => Object.fromEntries(SOLARS.map((s) => [s.id, s])), [SOLARS]);
    const CANOPY_MAP = useMemo(() => Object.fromEntries(CANOPY_TYPES.map((c) => [c.id, c])), [CANOPY_TYPES]);
    const COLOR_MAP = useMemo(() => Object.fromEntries(CANOPY_COLORS.map((c) => [c.id, c])), [CANOPY_COLORS]);

    const [size, setSize] = useState("1.98m");
    const [canopy, setCanopy] = useState({ type: "awning", color: "navy_gold" });
    const [primary, setPrimary] = useState(null); // single string id or null
    const [extras, setExtras] = useState([]);
    const [battery, setBattery] = useState("none");
    const [solar, setSolar] = useState("none");
    const [lighting, setLighting] = useState([]);
    const [brandName, setBrandName] = useState("");
    const [form, setForm] = useState({ name: "", email: "", phone: "", cidade: "", notes: "" });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [submittedId, setSubmittedId] = useState(null);
    const [view3d, setView3d] = useState(false);
    const [proposalIds, setProposalIds] = useState([]);

    const selectedSize = useMemo(() => SIZES.find((s) => s.id === size), [size, SIZES]);
    const selectedCanopyType = CANOPY_MAP[canopy.type];
    const selectedColor = COLOR_MAP[canopy.color];
    const selectedBattery = BATTERY_MAP[battery];
    const selectedSolar = SOLAR_MAP[solar];
    const selectedPrimary = primary ? PRIMARY_MAP[primary] : null;

    function toggleExtra(id) {
        setExtras((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
    }
    function toggleLighting(id) {
        setLighting((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
    }
    function selectPrimary(id) {
        setPrimary((cur) => (cur === id ? null : id));
    }
    function cycleCanopyColor() {
        const ids = CANOPY_COLORS.map((c) => c.id);
        const idx = ids.indexOf(canopy.color);
        const next = ids[(idx + 1) % ids.length];
        setCanopy((c) => ({ ...c, color: next }));
    }

    function onChange(e) {
        const { name, value } = e.target;
        // Phone: keep only digits, max 9 chars
        if (name === "phone") {
            const digits = value.replace(/\D/g, "").slice(0, 9);
            setForm((f) => ({ ...f, phone: digits }));
            setErrors((er) => ({ ...er, phone: undefined }));
            return;
        }
        setForm((f) => ({ ...f, [name]: value }));
        setErrors((er) => ({ ...er, [name]: undefined }));
    }

    function validateForm() {
        const er = {};
        if (!form.name.trim()) er.name = tr("Nome obrigatório.", "Name is required.");
        if (!form.email.trim()) {
            er.email = tr("Email obrigatório.", "Email is required.");
        } else if (!EMAIL_REGEX.test(form.email.trim())) {
            er.email = tr("Email inválido (ex.: nome@dominio.pt).", "Invalid email (e.g. name@domain.com).");
        }
        if (form.phone && !PHONE_REGEX.test(form.phone)) {
            er.phone = tr("Telemóvel deve ter exactamente 9 dígitos.", "Phone must be exactly 9 digits.");
        }
        if (!form.cidade) {
            er.cidade = tr("Selecione uma cidade.", "Select a city.");
        }
        setErrors(er);
        return Object.keys(er).length === 0;
    }

    async function submit(e) {
        e.preventDefault();
        if (!validateForm()) {
            toast.error(tr("Verifique os campos assinalados.", "Please check the highlighted fields."));
            return;
        }
        const addons = [...(primary ? [primary] : []), ...extras];
        setLoading(true);
        try {
            const { data } = await api.post("/cart-configurator", {
                size,
                addons,
                canopy,
                battery,
                solar,
                lighting,
                brand_name: brandName.trim() || null,
                branding_proposal_ids: proposalIds,
                name: form.name,
                email: form.email,
                phone: form.phone || null,
                cidade: form.cidade || null,
                notes: form.notes || null,
            });
            toast.success(data.message || tr("Configuração enviada!", "Configuration sent!"));
            setSubmittedId(data.id || null);
            setSubmitted(true);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = Array.isArray(detail) ? detail[0]?.msg : typeof detail === "string" ? detail : tr("Erro ao enviar configuração.", "Error sending configuration.");
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    }

    function reset() {
        setSize("1.98m");
        setCanopy({ type: "awning", color: "navy_gold" });
        setPrimary(null);
        setExtras([]);
        setBattery("none");
        setSolar("none");
        setLighting([]);
        setBrandName("");
        setForm({ name: "", email: "", phone: "", cidade: "", notes: "" });
        setErrors({});
        setSubmitted(false);
        setSubmittedId(null);
        setProposalIds([]);
    }

    /* Sticky CTA bar visibility — only after user scrolls past the hero */
    const [stickyVisible, setStickyVisible] = useState(false);
    useEffect(() => {
        const onScroll = () => setStickyVisible(window.scrollY > 360);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    /* Theme preset from URL — e.g. /simulador?tema=cafes pre-selects the
       matching primary equipment and brand name. Runs once on mount. */
    const [searchParams, setSearchParams] = useSearchParams();
    useEffect(() => {
        const tema = searchParams.get("tema");
        const brandOverride = (searchParams.get("brand") || "").trim();
        if (!tema && !brandOverride) return;
        const preset = tema ? THEME_PRESETS[tema] : null;
        if (preset?.primary && PRIMARY_MAP[preset.primary]) {
            setPrimary(preset.primary);
        }
        // Brand override takes precedence over theme preset brand.
        const finalBrand = brandOverride || preset?.brand || "";
        if (finalBrand) setBrandName(finalBrand);
        if (finalBrand) {
            toast.success(`Configuração inicial: ${finalBrand}`, {
                description: "Ajuste todos os detalhes abaixo.",
                duration: 3500,
            });
        }
        const next = new URLSearchParams(searchParams);
        next.delete("tema");
        next.delete("brand");
        setSearchParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const scrollToForm = () => {
        const el = document.getElementById("simulator-form");
        if (el) {
            const offset = 80;
            window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - offset, behavior: "smooth" });
        }
    };

    /* Build a brief summary chip used in the sticky bar */
    const summaryParts = [
        SIZES.find((s) => s.id === size)?.title,
        primary ? PRIMARY_MAP[primary]?.label : null,
        battery !== "none" ? BATTERY_MAP[battery]?.label : null,
    ].filter(Boolean);
    const summary = summaryParts.join(" · ");

    return (
        <>
            {/* Sticky CTA bar */}
            <div
                data-testid="simulator-sticky-cta"
                aria-hidden={!stickyVisible}
                className={cn(
                    "sticky top-16 z-30 border-b border-border bg-white/95 backdrop-blur-md shadow-sm transition-all duration-300",
                    stickyVisible ? "opacity-100 translate-y-0" : "pointer-events-none opacity-0 -translate-y-full"
                )}
            >
                <div className="container-x flex items-center justify-between gap-3 py-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <span className="hidden sm:inline-flex items-center gap-1 rounded-full bg-secondary/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-primary">
                            <Sparkles className="h-3 w-3" /> {tr("A configurar", "Configuring")}
                        </span>
                        <span className="text-sm font-semibold text-primary truncate">
                            {summary || tr("Personalize o seu carrinho", "Customise your cart")}
                        </span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <a
                            href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="simulator-sticky-whatsapp"
                            className="hidden md:inline-flex items-center gap-1.5 rounded-md border border-primary/15 bg-white px-3 py-2 text-xs font-bold text-primary hover:bg-muted transition"
                        >
                            <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                        </a>
                        <button
                            type="button"
                            onClick={scrollToForm}
                            data-testid="simulator-sticky-cta-primary"
                            className="inline-flex items-center gap-1.5 rounded-md bg-secondary px-4 py-2 text-xs font-bold text-primary shadow-sm hover:bg-yellow-300 hover:-translate-y-0.5 transition"
                        >
                            {tr("Pedir orçamento", "Request a quote")} <ArrowRight className="h-3.5 w-3.5" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Hero */}
            <section className="relative overflow-hidden border-b border-border gradient-navy" data-testid="simulator-hero">
                <div className="absolute inset-0 grain-overlay opacity-40 pointer-events-none" />
                <div className="container-x relative hero-y text-white">
                    <span className="eyebrow text-secondary">{t("hero", "eyebrow", tr("Simulador interactivo", "Interactive configurator"))}</span>
                    <h1 className="mt-3 max-w-4xl display-xl">
                        {(() => {
                            const raw = t("hero", "title", tr("Configure o seu carrinho à medida.", "Build your cart, your way."));
                            const hl = t("hero", "title_highlight", tr("carrinho", "cart"));
                            if (hl && raw.includes(hl)) {
                                const parts = raw.split(hl);
                                return <>{parts[0]}<span className="text-secondary">{hl}</span>{parts.slice(1).join(hl)}</>;
                            }
                            return raw.replace("{hl}", hl);
                        })()}
                    </h1>
                    <p className="mt-3 max-w-2xl text-sm sm:text-base text-white/80 leading-relaxed">
                        {t("hero", "subtitle", tr(
                            "Tamanho, cobertura, equipamento, energia e branding — tudo em tempo real na pré-visualização.",
                            "Size, canopy, equipment, energy and branding — all in real time in the live preview."
                        ))}
                    </p>
                </div>
            </section>

            {/* Configurator */}
            <section className="container-x section-y-sm" data-testid="simulator-section">
                <div className="grid lg:grid-cols-12 gap-6 sm:gap-10">
                    {/* LEFT — Steps */}
                    <div className="lg:col-span-7 space-y-12">
                        {/* Step 1 — Size */}
                        <div data-testid="step-size">
                            <StepHeader number={1} title={tr("Tamanho", "Size")} subtitle={tr("Bancada de 90 cm em ambos. Selecione o comprimento.", "90 cm counter on both. Choose the length.")} />
                            <div className="mt-6 grid sm:grid-cols-2 gap-4">
                                {SIZES.map((s) => (
                                    <SizeCard key={s.id} s={s} active={size === s.id} onClick={() => setSize(s.id)} />
                                ))}
                            </div>
                        </div>

                        {/* Step 2 — Canopy */}
                        <div data-testid="step-canopy">
                            <StepHeader number={2} title={tr("Cobertura sol & chuva", "Sun & rain canopy")} subtitle={tr("Escolha o tipo de toldo e a paleta.", "Choose the awning type and palette.")} />
                            <div className="mt-6 grid sm:grid-cols-2 gap-3">
                                {CANOPY_TYPES.map((c) => (
                                    <OptionCard
                                        key={c.id}
                                        active={canopy.type === c.id}
                                        icon={c.icon}
                                        label={c.label}
                                        hint={c.hint}
                                        onClick={() => setCanopy((s) => ({ ...s, type: c.id }))}
                                        testId={`canopy-type-${c.id}`}
                                    />
                                ))}
                            </div>

                            {canopy.type !== "none" && (
                                <div className="mt-5 rounded-xl border border-border bg-white p-5" data-testid="canopy-colors">
                                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">
                                        <Palette className="h-3.5 w-3.5" /> Paleta
                                    </div>
                                    <div className="mt-3 flex flex-wrap gap-2.5">
                                        {CANOPY_COLORS.map((c) => {
                                            const active = canopy.color === c.id;
                                            return (
                                                <button
                                                    key={c.id}
                                                    type="button"
                                                    onClick={() => setCanopy((s) => ({ ...s, color: c.id }))}
                                                    data-testid={`canopy-color-${c.id}`}
                                                    title={c.label}
                                                    className={cn(
                                                        "flex items-center gap-2 rounded-full border-2 pl-1 pr-3 py-1 transition",
                                                        active ? "border-primary bg-primary/[0.04]" : "border-border hover:border-primary/50"
                                                    )}
                                                >
                                                    <span className="relative inline-flex h-7 w-7 rounded-full overflow-hidden border border-black/10 flex-shrink-0">
                                                        <span className="absolute inset-0" style={{ background: c.primary }} />
                                                        <span className="absolute inset-y-0 right-0 w-1/2" style={{ background: c.accent }} />
                                                    </span>
                                                    <span className="text-xs font-bold text-primary">{c.label}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <p className="mt-3 text-xs text-muted-foreground">
                                        Dica: clique directamente sobre a cobertura na pré-visualização para ciclar entre paletas.
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Step 3 — Equipment */}
                        <div data-testid="step-equipment">
                            <StepHeader
                                number={3}
                                title={tr("Equipamento", "Equipment")}
                                subtitle={tr("Escolha 1 equipamento principal e adicione extras.", "Pick 1 main equipment and add extras.")}
                            />

                            <div className="mt-6">
                                <div className="flex items-baseline justify-between">
                                    <div className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">
                                        {tr("Principal (escolha 1)", "Main (pick 1)")}
                                    </div>
                                    {primary && (
                                        <button
                                            type="button"
                                            onClick={() => setPrimary(null)}
                                            data-testid="clear-primary"
                                            className="text-xs text-muted-foreground hover:text-primary"
                                        >
                                            Limpar
                                        </button>
                                    )}
                                </div>
                                <div className="mt-3 grid sm:grid-cols-2 gap-3">
                                    {PRIMARY_EQUIPMENT.map((p) => (
                                        <OptionCard
                                            key={p.id}
                                            active={primary === p.id}
                                            icon={p.icon}
                                            label={p.label}
                                            hint={p.hint}
                                            exclusive
                                            onClick={() => selectPrimary(p.id)}
                                            testId={`primary-${p.id}`}
                                        />
                                    ))}
                                </div>
                            </div>

                            <div className="mt-6">
                                <div className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">
                                    Extras (opcionais)
                                </div>
                                <div className="mt-3 grid sm:grid-cols-2 gap-3">
                                    {EXTRAS.map((e) => (
                                        <OptionCard
                                            key={e.id}
                                            active={extras.includes(e.id)}
                                            icon={e.icon}
                                            label={e.label}
                                            hint={e.hint}
                                            onClick={() => toggleExtra(e.id)}
                                            testId={`extra-${e.id}`}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Step 4 — Energy & Lighting */}
                        <div data-testid="step-energy">
                            <StepHeader number={4} title={tr("Energia & iluminação", "Energy & lighting")} subtitle={tr("Autonomia, recarga solar e ambientação luminosa.", "Autonomy, solar recharge and light ambience.")} />

                            <div className="mt-6">
                                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">
                                    <BatteryCharging className="h-3.5 w-3.5" /> {tr("Bateria de lítio", "Lithium battery")}
                                </div>
                                <div className="mt-3 grid sm:grid-cols-2 gap-3">
                                    {BATTERIES.map((b) => (
                                        <LevelCard
                                            key={b.id}
                                            active={battery === b.id}
                                            onClick={() => setBattery(b.id)}
                                            title={b.label}
                                            short={b.short}
                                            hint={b.hint}
                                            bars={b.bars}
                                            testId={`battery-${b.id}`}
                                        />
                                    ))}
                                </div>
                            </div>

                            <div className="mt-6">
                                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">
                                    <Sun className="h-3.5 w-3.5" /> Painel solar
                                </div>
                                <div className="mt-3 grid sm:grid-cols-2 gap-3">
                                    {SOLARS.map((s) => (
                                        <LevelCard
                                            key={s.id}
                                            active={solar === s.id}
                                            onClick={() => setSolar(s.id)}
                                            title={s.label}
                                            short={s.short}
                                            hint={s.hint}
                                            bars={Math.round(s.widthPct / 33.4)}
                                            testId={`solar-${s.id}`}
                                        />
                                    ))}
                                </div>
                            </div>

                            <div className="mt-6">
                                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">
                                    <Lightbulb className="h-3.5 w-3.5" /> {tr("Iluminação (cumulativo)", "Lighting (cumulative)")}
                                </div>
                                <div className="mt-3 grid sm:grid-cols-3 gap-3">
                                    {LIGHTING.map((l) => (
                                        <OptionCard
                                            key={l.id}
                                            active={lighting.includes(l.id)}
                                            icon={Lightbulb}
                                            label={l.label}
                                            hint={l.hint}
                                            onClick={() => toggleLighting(l.id)}
                                            testId={`light-${l.id}`}
                                            compact
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Step 5 — Branding */}
                        <div data-testid="step-branding">
                            <StepHeader
                                number={5}
                                title="Branding"
                                subtitle={tr("Reserve espaço para a sua marca. O nome aparece em tempo real no carrinho.", "Reserve space for your brand. The name appears in real time on the cart.")}
                            />
                            <div className="mt-6 rounded-xl border border-border bg-white p-5">
                                <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">
                                    <Type className="h-3.5 w-3.5" /> {tr("Nome da marca", "Brand name")}
                                </label>
                                <input
                                    type="text"
                                    maxLength={MAX_BRAND}
                                    value={brandName}
                                    onChange={(e) => setBrandName(e.target.value)}
                                    data-testid="brand-name-input"
                                    placeholder="Ex: NOVIFOOD, Café do João, Pizza Bella..."
                                    className="mt-2 w-full rounded-md border border-border bg-muted px-4 py-3 font-heading text-lg font-extrabold uppercase tracking-wider text-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary"
                                />
                                <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                                    <span>{tr("Aparece na bainha do toldo e no painel frontal.", "Appears on the awning hem and front panel.")}</span>
                                    <span className="font-mono">{brandName.length}/{MAX_BRAND}</span>
                                </div>
                                {/* Preset chips — quick brand names */}
                                <div className="mt-3 flex flex-wrap gap-1.5">
                                    <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground w-full">
                                        {tr("Sugestões rápidas", "Quick suggestions")}
                                    </span>
                                    {["BAR", "CAFÉ", "GELATO", "PIPOCAS", "CREPES", "DELI", "SUSHI", "PADARIA", "CHURROS"].map((preset) => {
                                        const active = brandName.trim().toUpperCase() === preset;
                                        return (
                                            <button
                                                key={preset}
                                                type="button"
                                                onClick={() => setBrandName(preset)}
                                                data-testid={`brand-preset-${preset.toLowerCase()}`}
                                                className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider transition ${
                                                    active
                                                        ? "border-primary bg-primary text-primary-foreground"
                                                        : "border-border bg-white text-primary hover:border-primary/40 hover:bg-muted"
                                                }`}
                                            >
                                                {preset}
                                            </button>
                                        );
                                    })}
                                    {brandName && (
                                        <button
                                            type="button"
                                            onClick={() => setBrandName("")}
                                            data-testid="brand-preset-clear"
                                            className="rounded-full border border-transparent px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground hover:text-primary hover:underline"
                                        >
                                            {tr("Limpar", "Clear")}
                                        </button>
                                    )}
                                </div>
                                <p className="mt-3 text-xs text-muted-foreground leading-relaxed">
                                    {tr(
                                        "Zonas de branding reservadas no projecto final: painel frontal (cliente), bainha do toldo (identificação à distância), laterais (logo e cores) e placa superior (LED opcional).",
                                        "Branding zones reserved in the final project: front panel (customer-facing), awning hem (distance identification), sides (logo and colours) and top sign (optional LED)."
                                    )}
                                </p>
                            </div>

                            <BrandingProposal model={size === "1.35m" ? "alfa" : "omega"} brandName={brandName} onGenerated={(pid) => setProposalIds((p) => [...p, pid])} />
                        </div>

                        {/* Step 6 — Contact */}
                        <div data-testid="step-contact">
                            <StepHeader number={6} title={tr("Receba o orçamento", "Get your quote")} subtitle={tr("Resposta personalizada em 48h.", "Personalised reply within 48h.")} />
                            {submitted ? (
                                <div className="mt-6 rounded-2xl border-2 border-primary bg-primary/[0.03] p-6 sm:p-8 text-center" data-testid="simulator-success">
                                    <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-secondary text-primary">
                                        <Check className="h-7 w-7" />
                                    </div>
                                    <h3 className="mt-4 font-heading text-2xl font-extrabold text-primary">{tr("Configuração enviada!", "Configuration sent!")}</h3>
                                    <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
                                        {tr("Recebemos os seus dados e a nossa equipa irá enviar um orçamento personalizado para ", "We've received your details and our team will send a personalised quote to ")}<strong className="text-primary">{form.email}</strong>{tr(" em 48 horas.", " within 48 hours.")}
                                    </p>
                                    <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                                        {submittedId && (
                                            <a
                                                href={`${API}/cart-configurator/${submittedId}/pdf`}
                                                data-testid="simulator-download-pdf"
                                                className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-xs font-bold text-primary-foreground hover:-translate-y-0.5 transition"
                                            >
                                                <FileDown className="h-4 w-4" /> {tr("Descarregar orçamento (PDF)", "Download quote (PDF)")}
                                            </a>
                                        )}
                                        {submittedId && (
                                            <button
                                                type="button"
                                                data-testid="simulator-share-config"
                                                onClick={async () => {
                                                    const url = `${window.location.origin}/api/share/${submittedId}`;
                                                    const text = tr("Vê o food cart que configurei na NOVIFOOD!", "Check out the food cart I configured at NOVIFOOD!");
                                                    if (navigator.share) {
                                                        try { await navigator.share({ title: "NOVIFOOD", text, url }); return; } catch { /* cancelled */ }
                                                    }
                                                    try {
                                                        await navigator.clipboard.writeText(url);
                                                        toast.success(tr("Link copiado! Partilha com quem quiseres.", "Link copied! Share it with anyone."));
                                                    } catch {
                                                        toast.info(tr("Copie o link: ", "Copy the link: ") + url, { duration: 10000 });
                                                    }
                                                }}
                                                className="inline-flex items-center gap-2 rounded-md bg-secondary px-5 py-2.5 text-xs font-bold text-primary hover:-translate-y-0.5 transition"
                                            >
                                                <Share2 className="h-4 w-4" /> {tr("Partilhar configuração", "Share configuration")}
                                            </button>
                                        )}
                                        <button onClick={reset} data-testid="simulator-reset"
                                            className="inline-flex items-center gap-2 rounded-md border border-primary px-5 py-2.5 text-xs font-bold text-primary hover:bg-primary hover:text-primary-foreground transition">
                                            {tr("Configurar outro carrinho", "Configure another cart")}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <form onSubmit={submit} id="simulator-form" noValidate className="mt-6 rounded-2xl border border-border bg-white p-6 lg:p-6 sm:p-8" data-testid="simulator-form">
                                    <div className="grid gap-5 sm:grid-cols-2">
                                        <Field label={tr("Nome", "Name")} name="name" value={form.name} onChange={onChange} required error={errors.name} testid="simulator-input-name" />
                                        <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required error={errors.email} testid="simulator-input-email" />
                                        <Field
                                            label={tr("Telemóvel (9 dígitos)", "Phone (9 digits)")}
                                            name="phone"
                                            type="tel"
                                            inputMode="numeric"
                                            pattern="\d{9}"
                                            maxLength={9}
                                            placeholder="912345678"
                                            value={form.phone}
                                            onChange={onChange}
                                            error={errors.phone}
                                            testid="simulator-input-phone"
                                        />
                                        <SelectField
                                            label={tr("Cidade", "City")}
                                            name="cidade"
                                            value={form.cidade}
                                            onChange={onChange}
                                            required
                                            error={errors.cidade}
                                            options={CIDADES_PT}
                                            placeholder={tr("Seleccione a cidade…", "Select a city…")}
                                            testid="simulator-input-cidade"
                                        />
                                    </div>
                                    <div className="mt-5">
                                        <label className="block text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">{tr("Notas (opcional)", "Notes (optional)")}</label>
                                        <textarea
                                            name="notes" rows={4} value={form.notes} onChange={onChange}
                                            data-testid="simulator-input-notes"
                                            placeholder={tr("Cores preferidas, detalhes do branding, prazo desejado...", "Preferred colours, branding details, desired timeline...")}
                                            className="mt-2 w-full rounded-md border border-border bg-muted px-4 py-3 text-sm text-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary"
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        data-testid="simulator-submit"
                                        className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3.5 text-sm font-bold text-primary-foreground transition hover:-translate-y-0.5 disabled:opacity-60"
                                    >
                                        {loading ? tr("A enviar...", "Sending...") : tr("Pedir orçamento", "Request a quote")} <Send className="h-4 w-4" />
                                    </button>
                                    <p className="mt-3 text-xs text-muted-foreground">
                                        {tr("Ao enviar, aceita ser contactado pela NOVIFOOD com a proposta personalizada.", "By submitting, you agree to be contacted by NOVIFOOD with the personalised proposal.")}
                                    </p>
                                </form>
                            )}
                        </div>
                    </div>

                    {/* RIGHT — Live Preview */}
                    <aside className="lg:col-span-5">
                        <div className="lg:sticky lg:top-24 space-y-5" data-testid="simulator-preview">
                            <div className="flex items-center justify-between">
                                <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-muted-foreground">
                                    {tr("Pré-visualização", "Live preview")}
                                </span>
                                <div className="inline-flex rounded-full border border-border bg-white p-1" data-testid="preview-toggle">
                                    {[{ id: false, label: "2D" }, { id: true, label: "3D" }].map((v) => (
                                        <button
                                            key={v.label}
                                            type="button"
                                            data-testid={`preview-toggle-${v.label.toLowerCase()}`}
                                            onClick={() => setView3d(v.id)}
                                            className={cn(
                                                "rounded-full px-3.5 py-1 text-[11px] font-extrabold transition",
                                                view3d === v.id ? "bg-primary text-white" : "text-primary/60 hover:text-primary"
                                            )}
                                        >
                                            {v.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {view3d ? (
                                <Cart3D
                                    sizeId={size}
                                    canopyType={canopy.type}
                                    colors={selectedColor}
                                    hasSolar={solar !== "none"}
                                    hasLighting={lighting.length > 0}
                                    hasFreezer={extras.includes("freezer")}
                                />
                            ) : (
                                <CartVisual
                                    size={selectedSize}
                                    canopyType={canopy.type}
                                    canopyColor={selectedColor}
                                    primary={selectedPrimary}
                                    hasFreezer={extras.includes("freezer")}
                                    battery={selectedBattery}
                                    solar={selectedSolar}
                                    lighting={lighting}
                                    brandName={brandName}
                                    onCanopyClick={cycleCanopyColor}
                                />
                            )}
                            <Summary
                                size={selectedSize}
                                canopyType={selectedCanopyType}
                                canopyColor={selectedColor}
                                canopyIsNone={canopy.type === "none"}
                                primary={selectedPrimary}
                                extras={extras}
                                battery={selectedBattery}
                                solar={selectedSolar}
                                lighting={lighting}
                                lightingOptions={LIGHTING}
                                brandName={brandName}
                            />
                        </div>
                    </aside>
                </div>
            </section>
        </>
    );
}

/* ------------------------------------------------------------------ */
/*  SUPPORT SUB-COMPONENTS                                            */
/* ------------------------------------------------------------------ */

function StepHeader({ number, title, subtitle }) {
    return (
        <div className="flex items-end gap-4">
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-foreground font-heading text-lg font-extrabold flex-shrink-0">
                {number}
            </div>
            <div>
                <h2 className="font-heading text-2xl lg:text-3xl font-extrabold text-primary">{title}</h2>
                <p className="text-sm text-muted-foreground">{subtitle}</p>
            </div>
        </div>
    );
}

function Field({ label, name, value, onChange, type = "text", required, testid, error, placeholder, inputMode, pattern, maxLength }) {
    return (
        <div>
            <label className="block text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">
                {label}{required && <span className="ml-1 text-red-500">*</span>}
            </label>
            <input
                name={name}
                type={type}
                value={value}
                required={required}
                onChange={onChange}
                placeholder={placeholder}
                inputMode={inputMode}
                pattern={pattern}
                maxLength={maxLength}
                aria-invalid={!!error}
                data-testid={testid}
                className={cn(
                    "mt-2 w-full rounded-md border bg-muted px-4 py-3 text-sm text-primary focus:bg-white focus:outline-none focus:ring-2",
                    error
                        ? "border-red-500 focus:ring-red-500"
                        : "border-border focus:ring-primary"
                )}
            />
            {error && (
                <p className="mt-1 text-xs text-red-600" data-testid={`${testid}-error`}>{error}</p>
            )}
        </div>
    );
}

function SelectField({ label, name, value, onChange, required, testid, error, options = [], placeholder = "Seleccionar…" }) {
    return (
        <div>
            <label className="block text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">
                {label}{required && <span className="ml-1 text-red-500">*</span>}
            </label>
            <select
                name={name}
                value={value}
                required={required}
                onChange={onChange}
                aria-invalid={!!error}
                data-testid={testid}
                className={cn(
                    "mt-2 w-full rounded-md border bg-muted px-4 py-3 text-sm text-primary focus:bg-white focus:outline-none focus:ring-2",
                    error
                        ? "border-red-500 focus:ring-red-500"
                        : "border-border focus:ring-primary"
                )}
            >
                <option value="">{placeholder}</option>
                {options.map((o) => (
                    <option key={o} value={o}>{o}</option>
                ))}
            </select>
            {error && (
                <p className="mt-1 text-xs text-red-600" data-testid={`${testid}-error`}>{error}</p>
            )}
        </div>
    );
}

function SizeCard({ s, active, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={`size-option-${s.id}`}
            className={cn(
                "relative text-left rounded-2xl border-2 p-6 transition hover:-translate-y-1 hover:shadow-lg bg-white",
                active ? "border-primary ring-4 ring-primary/10" : "border-border"
            )}
        >
            {active && (
                <span className="absolute top-4 right-4 inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground">
                    <Check className="h-4 w-4" />
                </span>
            )}
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
                <Ruler className="h-3.5 w-3.5" /> {s.title}
            </div>
            <div className="mt-3 font-heading text-3xl font-extrabold text-primary">{s.length}</div>
            <div className="mt-1 text-xs text-muted-foreground">Bancada {s.counter} · {s.capacity}</div>
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{s.description}</p>
        </button>
    );
}

function OptionCard({ active, icon, label, hint, onClick, testId, exclusive, compact }) {
    const Icon = typeof icon === "string" ? resolveIcon(icon, "Sparkles") : icon;
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className={cn(
                "group flex items-start gap-3 rounded-xl border-2 p-4 transition bg-white text-left",
                compact ? "flex-col items-start gap-2" : "",
                active ? "border-primary bg-primary/[0.03]" : "border-border hover:border-primary/40"
            )}
        >
            <div className={cn(
                "inline-flex h-11 w-11 items-center justify-center rounded-lg transition flex-shrink-0",
                active ? "bg-secondary text-primary" : "bg-muted text-primary group-hover:bg-secondary/60"
            )}>
                <Icon className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0 w-full">
                <div className="flex items-center justify-between gap-2">
                    <span className="font-heading font-bold text-primary text-sm">{label}</span>
                    <span className={cn(
                        "inline-flex h-5 w-5 items-center justify-center flex-shrink-0",
                        exclusive ? "rounded-full" : "rounded-full",
                        "border-2",
                        active ? "bg-primary border-primary text-primary-foreground" : "border-border"
                    )}>
                        {active && <Check className="h-3 w-3" />}
                    </span>
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>
            </div>
        </button>
    );
}

function LevelCard({ active, onClick, title, short, hint, bars, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className={cn(
                "group flex items-start gap-3 rounded-xl border-2 p-4 transition bg-white text-left",
                active ? "border-primary bg-primary/[0.03]" : "border-border hover:border-primary/40"
            )}
        >
            <div className={cn(
                "inline-flex h-11 w-11 items-center justify-center rounded-lg transition flex-shrink-0",
                active ? "bg-secondary text-primary" : "bg-muted text-primary group-hover:bg-secondary/60"
            )}>
                <BarsIndicator value={bars} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                    <span className="font-heading font-bold text-primary text-sm">{title}</span>
                    <span className={cn(
                        "inline-flex h-5 w-5 items-center justify-center rounded-full border-2 flex-shrink-0",
                        active ? "bg-primary border-primary text-primary-foreground" : "border-border"
                    )}>
                        {active && <Check className="h-3 w-3" />}
                    </span>
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">
                    {short ? <><span className="font-bold text-primary">{short}</span> · </> : null}{hint}
                </p>
            </div>
        </button>
    );
}

function BarsIndicator({ value }) {
    return (
        <div className="flex items-end gap-[2px] h-5">
            {[1, 2, 3].map((i) => (
                <span
                    key={i}
                    className={cn(
                        "w-1.5 rounded-sm transition-all",
                        i <= value ? "bg-current" : "bg-current/25",
                    )}
                    style={{ height: `${30 + i * 20}%` }}
                />
            ))}
        </div>
    );
}

function Summary({ size, canopyType, canopyColor, canopyIsNone, primary, extras, battery, solar, lighting, lightingOptions = [], brandName }) {
    const { tr } = useLocale();
    const hasFreezer = extras.includes("freezer");
    return (
        <div className="rounded-2xl border border-border bg-white p-6">
            <div className="flex items-center justify-between">
                <span className="eyebrow">{tr("Resumo", "Summary")}</span>
                <span className="inline-flex items-center gap-1 text-xs font-bold text-primary">
                    <Sparkles className="h-3.5 w-3.5 text-secondary" />
                    {tr("Sem compromisso", "No commitment")}
                </span>
            </div>

            <SumRow label={tr("Tamanho", "Size")}>
                <div className="font-heading text-base font-extrabold text-primary">
                    {size.length} <span className="text-muted-foreground text-xs font-medium">· {size.title}</span>
                </div>
            </SumRow>

            <SumRow label={tr("Cobertura", "Canopy")}>
                <div className="flex items-center justify-between gap-3 w-full">
                    <div className="font-heading text-sm font-extrabold text-primary">{canopyType.label}</div>
                    {!canopyIsNone && (
                        <span className="inline-flex items-center gap-2 text-[11px] text-muted-foreground">
                            <span className="relative inline-block h-4 w-4 rounded-full overflow-hidden border border-black/10">
                                <span className="absolute inset-0" style={{ background: canopyColor.primary }} />
                                <span className="absolute inset-y-0 right-0 w-1/2" style={{ background: canopyColor.accent }} />
                            </span>
                            {canopyColor.label}
                        </span>
                    )}
                </div>
            </SumRow>

            <SumRow label={tr("Equipamento principal", "Main equipment")}>
                <div className="text-sm text-primary">
                    {primary ? primary.label : <span className="italic text-muted-foreground">{tr("Não escolhido", "Not chosen")}</span>}
                </div>
            </SumRow>

            {hasFreezer && (
                <SumRow label="Extras">
                    <div className="text-sm text-primary">{tr("Congelador", "Freezer")}</div>
                </SumRow>
            )}

            <SumRow label={tr("Energia", "Energy")}>
                <div className="text-sm text-primary flex flex-wrap items-center gap-x-3 gap-y-0.5">
                    <span className="inline-flex items-center gap-1"><BatteryCharging className="h-3.5 w-3.5" /> {battery.label}{battery.short !== "—" ? ` · ${battery.short}` : ""}</span>
                    <span className="inline-flex items-center gap-1"><Sun className="h-3.5 w-3.5" /> {solar.label}{solar.short !== "—" ? ` · ${solar.short}` : ""}</span>
                </div>
            </SumRow>

            <SumRow label={tr("Iluminação", "Lighting")}>
                {lighting.length === 0 ? (
                    <span className="text-sm italic text-muted-foreground">{tr("Nenhuma", "None")}</span>
                ) : (
                    <div className="text-sm text-primary flex flex-wrap gap-x-2 gap-y-0.5">
                        {lighting.map((id) => {
                            const l = lightingOptions.find((x) => x.id === id);
                            return <span key={id} className="inline-flex items-center gap-1"><Zap className="h-3.5 w-3.5 text-secondary" /> {l?.label}</span>;
                        })}
                    </div>
                )}
            </SumRow>

            <SumRow label="Branding" last>
                <div className="text-sm text-primary">
                    {brandName ? <span className="font-bold uppercase tracking-wider">{brandName}</span> : <span className="italic text-muted-foreground">{tr("Sem nome definido", "No name set")}</span>}
                </div>
            </SumRow>
        </div>
    );
}

function SumRow({ label, children, last }) {
    return (
        <div className={cn("mt-4", !last && "pb-4 border-b border-border")}>
            <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{label}</div>
            <div className="mt-1">{children}</div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  CART VISUAL — SVG-based, realistic, brand-aware                    */
/* ------------------------------------------------------------------ */

function CartVisual({ size, canopyType, canopyColor, primary, hasFreezer, battery, solar, lighting, brandName, onCanopyClick }) {
    const isCompact = size.id === "1.35m";
    const bodyWidth = isCompact ? 280 : 380;
    const bodyX = (640 - bodyWidth) / 2;
    const bodyY = 240;
    const bodyH = 130;

    const poleTop = 74;
    const poleBottomY = bodyY - 4;
    const leftPoleX = bodyX + 20;
    const rightPoleX = bodyX + bodyWidth - 20;

    const stripeId = `stripe-${canopyColor.id}`;
    const displayBrand = (brandName || "").slice(0, MAX_BRAND);
    const showBrandPlaceholder = !displayBrand;

    const isProtected = canopyType !== "none";

    return (
        <div
            className="rounded-2xl border border-border bg-gradient-to-br from-sky-50 via-white to-amber-50 p-4 overflow-hidden relative"
            data-testid="cart-visual"
        >
            <div className="absolute top-4 left-4 z-10 inline-flex items-center gap-1.5 rounded-full bg-white/80 backdrop-blur px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-primary shadow-sm">
                {isProtected ? <><Umbrella className="h-3 w-3 text-secondary" /> Protegido sol &amp; chuva</> : <><Sun className="h-3 w-3" /> Exposto ao tempo</>}
            </div>

            {lighting.length > 0 && (
                <div className="absolute top-4 right-4 z-10 inline-flex items-center gap-1 rounded-full bg-amber-400/90 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-primary shadow-sm">
                    <Zap className="h-3 w-3" /> {lighting.length} ilum.
                </div>
            )}

            <svg
                viewBox="0 0 640 440"
                xmlns="http://www.w3.org/2000/svg"
                className="w-full h-auto"
                role="img"
                aria-label="Pré-visualização do carrinho"
            >
                <defs>
                    <pattern id={stripeId} patternUnits="userSpaceOnUse" width="20" height="20">
                        <rect width="20" height="20" fill={canopyColor.primary} />
                        <rect x="10" width="10" height="20" fill={canopyColor.accent} />
                    </pattern>

                    <linearGradient id="body-grad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#FFFFFF" />
                        <stop offset="45%" stopColor="#F4F6F8" />
                        <stop offset="100%" stopColor="#DCE3EA" />
                    </linearGradient>

                    <linearGradient id="brand-panel-grad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#1E3A5F" />
                        <stop offset="100%" stopColor="#0F1F36" />
                    </linearGradient>

                    <radialGradient id="shadow-grad" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="rgba(30,58,95,0.35)" />
                        <stop offset="100%" stopColor="rgba(30,58,95,0)" />
                    </radialGradient>

                    <radialGradient id="wheel-grad" cx="35%" cy="35%" r="70%">
                        <stop offset="0%" stopColor="#475569" />
                        <stop offset="100%" stopColor="#0F172A" />
                    </radialGradient>

                    {/* Glow filter for lighting */}
                    <filter id="glow-yellow" x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur stdDeviation="3" result="blur" />
                        <feFlood floodColor="#FDE047" floodOpacity="1" />
                        <feComposite in2="blur" operator="in" />
                        <feMerge>
                            <feMergeNode />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>

                    {/* Spot cone gradient */}
                    <linearGradient id="spot-cone" x1="0.5" y1="0" x2="0.5" y2="1">
                        <stop offset="0%" stopColor="#FDE047" stopOpacity="0.75" />
                        <stop offset="100%" stopColor="#FDE047" stopOpacity="0" />
                    </linearGradient>

                    {/* Ambient RGB gradient */}
                    <linearGradient id="rgb-grad" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#F472B6" stopOpacity="0.7" />
                        <stop offset="50%" stopColor="#A78BFA" stopOpacity="0.7" />
                        <stop offset="100%" stopColor="#22D3EE" stopOpacity="0.7" />
                    </linearGradient>
                </defs>

                {/* Ground shadow */}
                <ellipse cx="320" cy="418" rx={bodyWidth / 2 + 40} ry="12" fill="url(#shadow-grad)" />

                {/* ============ CANOPY ============ */}
                <g
                    style={{ cursor: isProtected ? "pointer" : "default", transition: "opacity 300ms" }}
                    onClick={isProtected ? onCanopyClick : undefined}
                    data-testid={`visual-canopy-${canopyType}`}
                >
                    {isProtected && canopyType !== "umbrella" && (
                        <>
                            <rect x={leftPoleX - 2} y={poleTop} width="4" height={poleBottomY - poleTop} fill="#1E293B" rx="1" />
                            <rect x={rightPoleX - 2} y={poleTop} width="4" height={poleBottomY - poleTop} fill="#1E293B" rx="1" />
                            {/* Pole caps */}
                            <circle cx={leftPoleX} cy={poleTop} r="3" fill="#F59E0B" />
                            <circle cx={rightPoleX} cy={poleTop} r="3" fill="#F59E0B" />
                        </>
                    )}

                    {canopyType === "awning" && (
                        <g>
                            <rect x={leftPoleX - 8} y={poleTop - 5} width={rightPoleX - leftPoleX + 16} height="8" fill="#1E293B" rx="2" />
                            <path
                                d={`M ${leftPoleX - 10} ${poleTop - 2}
                                    L ${rightPoleX + 10} ${poleTop - 2}
                                    L ${rightPoleX + 26} ${poleTop + 52}
                                    L ${leftPoleX - 26} ${poleTop + 52} Z`}
                                fill={`url(#${stripeId})`}
                                stroke="#0F172A" strokeWidth="1.5"
                            />
                            {/* Valance (scalloped hem) with brand name */}
                            <g>
                                <path
                                    d={`M ${leftPoleX - 26} ${poleTop + 52}
                                        L ${rightPoleX + 26} ${poleTop + 52}
                                        L ${rightPoleX + 26} ${poleTop + 68}
                                        L ${leftPoleX - 26} ${poleTop + 68} Z`}
                                    fill={canopyColor.accent}
                                    stroke="#0F172A" strokeWidth="1"
                                />
                                {/* Scalloped bottom edge */}
                                {Array.from({ length: Math.max(10, Math.floor((rightPoleX - leftPoleX + 52) / 26)) }).map((_, i, arr) => {
                                    const w = (rightPoleX - leftPoleX + 52) / arr.length;
                                    const cx = leftPoleX - 26 + w * i + w / 2;
                                    return (
                                        <circle key={i} cx={cx} cy={poleTop + 68} r={w / 2 - 0.5} fill={canopyColor.accent} stroke="#0F172A" strokeWidth="0.8" />
                                    );
                                })}
                                {/* Brand text on valance */}
                                <text
                                    x={(leftPoleX + rightPoleX) / 2}
                                    y={poleTop + 63}
                                    textAnchor="middle"
                                    fontFamily="Inter, system-ui, sans-serif"
                                    fontWeight="900"
                                    fontSize={isCompact ? 11 : 13}
                                    letterSpacing="2"
                                    fill={showBrandPlaceholder ? "rgba(15,23,42,0.35)" : ["#F8FAFC", "#FBBF24", "#D6CDB2"].includes(canopyColor.accent) ? "#0F172A" : "#F8FAFC"}
                                    style={{ textTransform: "uppercase" }}
                                >
                                    {showBrandPlaceholder ? "[ A SUA MARCA ]" : displayBrand}
                                </text>
                            </g>
                        </g>
                    )}

                    {canopyType === "flat" && (
                        <g>
                            <rect
                                x={leftPoleX - 16} y={poleTop - 6}
                                width={rightPoleX - leftPoleX + 32} height="46"
                                fill={`url(#${stripeId})`} stroke="#0F172A" strokeWidth="1.5" rx="2"
                            />
                            {/* Brand text inside canopy */}
                            <text
                                x={(leftPoleX + rightPoleX) / 2}
                                y={poleTop + 22}
                                textAnchor="middle"
                                fontFamily="Inter, system-ui, sans-serif"
                                fontWeight="900"
                                fontSize={isCompact ? 13 : 16}
                                letterSpacing="2.5"
                                fill={showBrandPlaceholder ? "rgba(248,250,252,0.45)" : canopyColor.accent}
                                style={{ textTransform: "uppercase" }}
                                stroke="#0F172A" strokeWidth="0.3"
                            >
                                {showBrandPlaceholder ? "[ A SUA MARCA ]" : displayBrand}
                            </text>
                            {/* Scalloped edge */}
                            <g transform={`translate(${leftPoleX - 16}, ${poleTop + 40})`}>
                                {Array.from({ length: 16 }).map((_, i) => (
                                    <circle
                                        key={i}
                                        cx={10 + i * ((rightPoleX - leftPoleX + 32) / 16)}
                                        cy="0"
                                        r="8"
                                        fill={canopyColor.accent}
                                        stroke="#0F172A" strokeWidth="1"
                                    />
                                ))}
                            </g>
                        </g>
                    )}

                    {canopyType === "umbrella" && (
                        <g>
                            <rect x={318} y={poleTop} width="4" height={poleBottomY - poleTop} fill="#1E293B" rx="1" />
                            <path
                                d={`M 140 ${poleTop + 24}
                                    Q 320 ${poleTop - 64} 500 ${poleTop + 24}
                                    L 500 ${poleTop + 32}
                                    Q 320 ${poleTop - 52} 140 ${poleTop + 32} Z`}
                                fill={`url(#${stripeId})`}
                                stroke="#0F172A" strokeWidth="1.5"
                            />
                            {[180, 220, 260, 300, 340, 380, 420, 460].map((x) => (
                                <line key={x} x1={x} y1={poleTop + 26} x2="320" y2={poleTop - 32} stroke="#0F172A" strokeWidth="0.7" opacity="0.4" />
                            ))}
                            <circle cx="320" cy={poleTop - 36} r="5" fill={canopyColor.accent} stroke="#0F172A" strokeWidth="1.2" />
                            {/* Brand text below dome */}
                            <text
                                x="320" y={poleTop + 24}
                                textAnchor="middle"
                                fontFamily="Inter, system-ui, sans-serif"
                                fontWeight="900"
                                fontSize={isCompact ? 10 : 12}
                                letterSpacing="2"
                                fill={showBrandPlaceholder ? "rgba(248,250,252,0.55)" : canopyColor.accent}
                                style={{ textTransform: "uppercase" }}
                                stroke="#0F172A" strokeWidth="0.3"
                            >
                                {showBrandPlaceholder ? "[ A SUA MARCA ]" : displayBrand}
                            </text>
                        </g>
                    )}
                </g>

                {/* ============ SOLAR PANEL ============ */}
                {solar.widthPct > 0 && canopyType !== "umbrella" && (() => {
                    const panelY = canopyType === "none" ? 36 : poleTop - 22;
                    const fullWidth = rightPoleX - leftPoleX - 20;
                    const panelW = (fullWidth * solar.widthPct) / 100;
                    const panelX = bodyX + (bodyWidth - panelW) / 2;
                    return (
                        <g data-testid={`visual-solar-${solar.id}`}>
                            <rect x={panelX} y={panelY} width={panelW} height="12" fill="#0F172A" stroke="#1E293B" strokeWidth="1" rx="1" />
                            {Array.from({ length: Math.max(6, Math.floor(panelW / 16)) }).map((_, i, arr) => (
                                <line
                                    key={i}
                                    x1={panelX + (panelW / arr.length) * i}
                                    y1={panelY}
                                    x2={panelX + (panelW / arr.length) * i}
                                    y2={panelY + 12}
                                    stroke="#1E293B" strokeWidth="0.8"
                                />
                            ))}
                            <line x1={panelX} y1={panelY + 6} x2={panelX + panelW} y2={panelY + 6} stroke="#1E293B" strokeWidth="0.6" opacity="0.6" />
                            <text x={panelX + panelW / 2} y={panelY - 3} textAnchor="middle" fontSize="8" fontWeight="800" fill="#1E3A5F" letterSpacing="1">
                                ☀ {solar.power}
                            </text>
                        </g>
                    );
                })()}

                {/* ============ SPOTS (lighting) — rendered BEHIND body ============ */}
                {lighting.includes("spots") && (
                    <g opacity="0.85" data-testid="visual-light-spots">
                        {[0.25, 0.5, 0.75].map((fr, i) => {
                            const cx = bodyX + bodyWidth * fr;
                            const topY = canopyType === "none" ? 90 : poleTop + 20;
                            return (
                                <polygon
                                    key={i}
                                    points={`${cx - 2},${topY} ${cx + 2},${topY} ${cx + 40},${bodyY + 44} ${cx - 40},${bodyY + 44}`}
                                    fill="url(#spot-cone)"
                                />
                            );
                        })}
                    </g>
                )}

                {/* ============ CART BODY ============ */}
                <g style={{ transition: "all 500ms cubic-bezier(0.4, 0, 0.2, 1)" }}>
                    {/* PERIMETER LED glow (behind top rack) */}
                    {lighting.includes("perimeter") && (
                        <rect
                            x={bodyX - 3} y={bodyY - 11}
                            width={bodyWidth + 6} height="5"
                            fill="#FDE047"
                            filter="url(#glow-yellow)"
                            data-testid="visual-light-perimeter"
                        />
                    )}

                    {/* Top rack amber */}
                    <rect x={bodyX} y={bodyY - 8} width={bodyWidth} height="10" fill="#F59E0B" rx="2" />
                    <rect x={bodyX} y={bodyY - 8} width={bodyWidth} height="3" fill="#FCD34D" rx="2" opacity="0.7" />

                    {/* Main body */}
                    <rect
                        x={bodyX} y={bodyY}
                        width={bodyWidth} height={bodyH}
                        fill="url(#body-grad)"
                        stroke="#1E3A5F" strokeWidth="2.5"
                        rx="6"
                    />

                    {/* Rivets on corners */}
                    {[[bodyX + 6, bodyY + 6], [bodyX + bodyWidth - 6, bodyY + 6], [bodyX + 6, bodyY + bodyH - 6], [bodyX + bodyWidth - 6, bodyY + bodyH - 6]].map(([cx, cy], i) => (
                        <circle key={i} cx={cx} cy={cy} r="1.5" fill="#1E3A5F" />
                    ))}

                    {/* Service counter dark band with primary equipment icon */}
                    <rect x={bodyX + 4} y={bodyY + 4} width={bodyWidth - 8} height="30" fill="#1E3A5F" rx="3" />
                    {primary ? (
                        <g transform={`translate(${bodyX + (bodyWidth - 32) / 2}, ${bodyY + 7})`}>
                            <rect width="32" height="24" rx="3" fill="#F59E0B" />
                            <foreignObject x="0" y="0" width="32" height="24">
                                <div style={{
                                    width: "100%", height: "100%",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    color: "#1E3A5F",
                                }}>
                                    {(() => {
                                        const EquipIcon = typeof primary.icon === "string" ? resolveIcon(primary.icon, "Sparkles") : primary.icon;
                                        return <EquipIcon size={16} strokeWidth={2.5} />;
                                    })()}
                                </div>
                            </foreignObject>
                        </g>
                    ) : (
                        <text
                            x={bodyX + bodyWidth / 2} y={bodyY + 22}
                            fill="rgba(255,255,255,0.45)"
                            fontSize="10" fontWeight="700"
                            textAnchor="middle"
                            style={{ letterSpacing: "1.5px", textTransform: "uppercase" }}
                        >
                            Bancada livre
                        </text>
                    )}

                    {/* Service window (with optional RGB glow) */}
                    <rect
                        x={bodyX + 20} y={bodyY + 44}
                        width={bodyWidth - 40} height="36"
                        fill={lighting.includes("ambient") ? "url(#rgb-grad)" : "#BAE6FD"}
                        stroke="#1E3A5F" strokeWidth="1.5"
                        rx="2" opacity="0.85"
                        data-testid={lighting.includes("ambient") ? "visual-light-ambient" : undefined}
                    />
                    <path
                        d={`M ${bodyX + 26} ${bodyY + 48} L ${bodyX + 40} ${bodyY + 48} L ${bodyX + 32} ${bodyY + 78} L ${bodyX + 26} ${bodyY + 78} Z`}
                        fill="rgba(255,255,255,0.55)"
                    />

                    {/* ============ FRONT BRAND PANEL ============ */}
                    {(() => {
                        const panelY = bodyY + 84;
                        const panelX = bodyX + 14;
                        const panelW = bodyWidth - 28;
                        const panelH = 30;
                        return (
                            <g data-testid="visual-brand-panel">
                                <rect
                                    x={panelX} y={panelY}
                                    width={panelW} height={panelH}
                                    fill="url(#brand-panel-grad)"
                                    stroke={canopyColor.accent} strokeWidth="2"
                                    rx="3"
                                />
                                {/* Inner border accent */}
                                <rect
                                    x={panelX + 4} y={panelY + 4}
                                    width={panelW - 8} height={panelH - 8}
                                    fill="none"
                                    stroke={canopyColor.accent} strokeWidth="0.6" strokeDasharray="2 2"
                                    opacity="0.5"
                                    rx="2"
                                />
                                <text
                                    x={panelX + panelW / 2}
                                    y={panelY + panelH / 2 + 5}
                                    textAnchor="middle"
                                    fontFamily="Inter, system-ui, sans-serif"
                                    fontWeight="900"
                                    fontSize={isCompact ? (displayBrand.length > 14 ? 11 : 15) : (displayBrand.length > 14 ? 14 : 18)}
                                    letterSpacing={isCompact ? 2 : 3}
                                    fill={showBrandPlaceholder ? "rgba(245,158,11,0.45)" : canopyColor.accent}
                                    style={{ textTransform: "uppercase" }}
                                >
                                    {showBrandPlaceholder ? "A TUA MARCA AQUI" : displayBrand}
                                </text>
                            </g>
                        );
                    })()}

                    {/* Side handle */}
                    <rect x={bodyX + bodyWidth - 6} y={bodyY + 54} width="3" height="22" rx="1.5" fill="#1E3A5F" />

                    {/* Bottom kick plate */}
                    <rect x={bodyX} y={bodyY + bodyH - 10} width={bodyWidth} height="10" fill="#1E3A5F" />

                    {/* Freezer badge */}
                    {hasFreezer && (
                        <g data-testid="visual-freezer" transform={`translate(${bodyX + bodyWidth - 92}, ${bodyY + bodyH + 6})`}>
                            <rect width="84" height="18" rx="9" fill="#0EA5E9" />
                            <foreignObject x="4" y="2" width="16" height="14">
                                <div style={{ color: "#F8FAFC" }}>
                                    <Snowflake size={14} strokeWidth={2.5} />
                                </div>
                            </foreignObject>
                            <text x="24" y="12" fill="#F8FAFC" fontSize="9" fontWeight="800" letterSpacing="1">
                                FREEZER -18°C
                            </text>
                        </g>
                    )}

                    {/* Battery pill with bars */}
                    {battery.bars > 0 && (() => {
                        const label = `${battery.label.toUpperCase()} · ${battery.capacity}`;
                        const pillW = 14 + 8 * battery.bars + label.length * 4.2;
                        return (
                            <g data-testid={`visual-battery-${battery.id}`} transform={`translate(${bodyX + 10}, ${bodyY + bodyH + 6})`}>
                                <rect width={pillW} height="18" rx="9" fill="#1E3A5F" />
                                <foreignObject x="4" y="2" width="16" height="14">
                                    <div style={{ color: "#F59E0B" }}>
                                        <BatteryCharging size={14} strokeWidth={2.5} />
                                    </div>
                                </foreignObject>
                                {[0, 1, 2].map((i) => (
                                    <rect
                                        key={i}
                                        x={22 + i * 6}
                                        y={i === 0 ? 9 : i === 1 ? 7 : 5}
                                        width="4"
                                        height={i === 0 ? 5 : i === 1 ? 7 : 9}
                                        rx="1"
                                        fill={i < battery.bars ? "#F59E0B" : "rgba(245,158,11,0.25)"}
                                    />
                                ))}
                                <text x={46} y="12" fill="#F59E0B" fontSize="9" fontWeight="800" letterSpacing="0.8">
                                    {label}
                                </text>
                            </g>
                        );
                    })()}
                </g>

                {/* ============ WHEELS ============ */}
                <g style={{ transition: "all 500ms cubic-bezier(0.4, 0, 0.2, 1)" }}>
                    {[{ cx: bodyX + 32 }, { cx: bodyX + bodyWidth - 32 }].map((w, i) => (
                        <g key={i}>
                            <circle cx={w.cx} cy="400" r="24" fill="url(#wheel-grad)" stroke="#0F172A" strokeWidth="2.2" />
                            {/* Tread */}
                            {Array.from({ length: 16 }).map((_, j) => {
                                const angle = (j / 16) * Math.PI * 2;
                                return (
                                    <line
                                        key={j}
                                        x1={w.cx + Math.cos(angle) * 22} y1={400 + Math.sin(angle) * 22}
                                        x2={w.cx + Math.cos(angle) * 24} y2={400 + Math.sin(angle) * 24}
                                        stroke="#0F172A" strokeWidth="1.2"
                                    />
                                );
                            })}
                            {/* Rim */}
                            <circle cx={w.cx} cy="400" r="17" fill="none" stroke="#F59E0B" strokeWidth="1.6" />
                            {/* Hub */}
                            <circle cx={w.cx} cy="400" r="6" fill="#F59E0B" stroke="#0F172A" strokeWidth="1.2" />
                            {/* Spokes */}
                            {[0, 45, 90, 135].map((deg) => (
                                <line
                                    key={deg}
                                    x1={w.cx + Math.cos((deg * Math.PI) / 180) * 17}
                                    y1={400 + Math.sin((deg * Math.PI) / 180) * 17}
                                    x2={w.cx - Math.cos((deg * Math.PI) / 180) * 17}
                                    y2={400 - Math.sin((deg * Math.PI) / 180) * 17}
                                    stroke="#F59E0B" strokeWidth="1.3" opacity="0.75"
                                />
                            ))}
                        </g>
                    ))}
                </g>

                {/* Dimension line */}
                <g opacity="0.7">
                    <line x1={bodyX} y1="375" x2={bodyX + bodyWidth} y2="375" stroke="#1E3A5F" strokeWidth="1" strokeDasharray="3 3" />
                    <line x1={bodyX} y1="370" x2={bodyX} y2="380" stroke="#1E3A5F" strokeWidth="1" />
                    <line x1={bodyX + bodyWidth} y1="370" x2={bodyX + bodyWidth} y2="380" stroke="#1E3A5F" strokeWidth="1" />
                </g>
            </svg>

            <div className="mt-2 text-center">
                <span className="inline-flex items-center gap-1 text-xs font-bold text-primary bg-secondary/50 rounded-full px-3 py-1">
                    <Ruler className="h-3 w-3" /> {size.length}
                </span>
            </div>
        </div>
    );
}
